import Info from "@assets/icons/info.svg";
import { useI18n } from "@core/i18n/i18n";
import { libraryFilterManager } from "@core/service-container";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { NetworkError } from "@ui/components/common/error";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { Tooltip } from "@ui/components/common/tooltip";
import { DROPPABLE_ID } from "@ui/pages/program-builder/drag-drop/program-builder-drag-drop-context";
import { useExerciseLibrary } from "@ui/pages/program-builder/hooks/use-exercise-library";
import { ExerciseLibrarySearchFilter } from "@ui/pages/program-builder/library/sidebar/exercise-library-search-filter";
import { ExerciseLibraryCounts } from "@ui/pages/program-builder/library/sidebar/exercise-library-counts";
import { DraggableExerciseCard, ExerciseCard } from "@ui/pages/program-builder/library/exercise-card";
import { colors } from "@ui/style/colors";
import { row, stack } from "@ui/style/layout";
import { getScrollbarStyle } from "@ui/style/scrollbar";
import { useObservable } from "micro-observables";
import React, { useEffect, useMemo } from "react";
import { Droppable } from "@hello-pangea/dnd";
import { OnboardingPanel } from "@ui/components/onboarding/onboarding-panel";
import { Exercise } from "@domain/exercise/exercise";
import { LibraryFilteringState } from "@domain/library/library-filter-manager";
import { EmptySearch } from "@ui/pages/exercise/components/empty-search";

export const ExerciseLibrary: React.FC<{
	areAlternativesEdited: boolean;
	showAddExerciseGuidance?: boolean;
	onHideAddExerciseGuidance?: () => void;
	showFilterLibraryGuidance?: boolean;
	onHideFilterLibraryGuidance?: () => void;
}> = ({ areAlternativesEdited, showAddExerciseGuidance, onHideAddExerciseGuidance, showFilterLibraryGuidance, onHideFilterLibraryGuidance }) => {

	const { format } = useI18n();
	const alreadySelectedExercises = useObservable(libraryFilterManager.exerciseWithAlternativesEditedList);

	const {
		filteredSelectedExercises: filteredPreSelectedExercises,
		filteredExercises,
		isAnyFilterActive,
		hasSelectedExercises: hasPreSelectedExercises,
		refreshLibraryExercises,
		openExerciseDetailsModal,
		isError,
		isLoading,
	} = useExerciseLibrary();

	useEffect(() => {
		// Keep complementary filtering state and filters when opening via Complementary flow
		if (libraryFilterManager.filteringState.get() !== LibraryFilteringState.COMPLEMENTARY) {
			libraryFilterManager.clearLibraryFiltersAndSearch();
		}
		refreshLibraryExercises();
	}, [refreshLibraryExercises]);

	const areFilteredExercisesNotPresent = useMemo(
		() =>
			isAnyFilterActive &&
			filteredPreSelectedExercises.length === 0 &&
			filteredExercises.length === 0 &&
			alreadySelectedExercises.length === 0,
		[isAnyFilterActive, filteredPreSelectedExercises.length, filteredExercises.length, alreadySelectedExercises.length],
	);

	const renderDraggableExerciseCard = (exercise: Exercise, index: number) => (
		<DraggableExerciseCard
			key={exercise.draggableId}
			exercise={{ ...exercise, alternativeExercises: [] }}
			index={index}
			uiStyle="row"
			onClick={() => openExerciseDetailsModal(exercise)}
			copy
			showAlternatives={!areAlternativesEdited}
		/>
	);

	let body: React.ReactNode;
	if (isLoading) {
		body = <StyledLoadingContainer />;
	} else if (isError) {
		body = (
			<StyledNetworkError
				withoutIcon
				description={format("exerciseLibrary.errorMessage")}
				onRefresh={refreshLibraryExercises}
			/>
		);
	} else {
		body = (
			<>
				<ExerciseLibrarySearchFilter
					showFilterLibraryGuidance={showFilterLibraryGuidance}
					onHideFilterLibraryGuidance={onHideFilterLibraryGuidance}
				/>
				{areFilteredExercisesNotPresent ? (
					<EmptySearchContainer>
						<EmptySearch />
					</EmptySearchContainer>
				) : (
					<>
						{!isAnyFilterActive && !hasPreSelectedExercises && <ExerciseLibraryCounts mode="LibraryAndAdded" />}
						<ScrollContainer>
							{filteredPreSelectedExercises.length > 0 && (
								<>
									<Section className="p-size-m-bold">{format("exerciseLibrary.selectedExercisesSection")}</Section>
									<StyledExerciseLibraryCount mode="PreSelected" />
									<Droppable droppableId={DROPPABLE_ID.PreSelectedExercise} isDropDisabled={true}>
										{provided => (
											<List ref={provided.innerRef}>
												{filteredPreSelectedExercises.map((exercise, index) =>
													renderDraggableExerciseCard(exercise, index),
												)}
												{provided.placeholder}
											</List>
										)}
									</Droppable>
								</>
							)}
							{filteredExercises.length > 0 && (
								<>
									{hasPreSelectedExercises && (
										<>
											<Section className="p-size-m-bold">{format("exerciseLibrary.librarySection")}</Section>
											<StyledExerciseLibraryCount mode="Library" />
										</>
									)}
									<Droppable droppableId={DROPPABLE_ID.Library} isDropDisabled={true}>
										{provided => (
											<List ref={provided.innerRef}>
												{showAddExerciseGuidance && filteredExercises.length > 0 && (
													<AddExerciseGuideTooltip
														interactive
														visible
														placement="right-start"
														offset={[12, 0]}
														arrow
														appendTo={(node: Element) => node.parentElement ?? document.body}
														maxWidth={360}
														onClickOutside={onHideAddExerciseGuidance}
														content={
															<OnboardingPanel
																text={format("programBuilder.quickstart.guide.addFirstExercise")}
																onClose={() => onHideAddExerciseGuidance?.()}
															/>
														}
													>
														<TooltipAnchor aria-hidden />
													</AddExerciseGuideTooltip>
												)}
												{filteredExercises.map((exercise, index) =>
													renderDraggableExerciseCard(exercise, index),
												)}
												{provided.placeholder}
											</List>
										)}
									</Droppable>
								</>
							)}
							{alreadySelectedExercises.length > 0 && (
								<>
									<AlreadySelectedHeader>
										<Sort>
											<div className="p-size-m-bold">{format("exerciseLibrary.alreadyAdded")}</div>
											<StyledTooltip
												content={format("exerciseLibrary.alreadyInSelectionToolTipMessage", {
													exerciseSelectedCount: alreadySelectedExercises.length,
												})}
											>
												<IconContainer>
													<Info width={22} height={22} color={colors.grey80} />
												</IconContainer>
											</StyledTooltip>
										</Sort>
										{hasPreSelectedExercises && <ExerciseLibraryCounts mode="Added" />}
									</AlreadySelectedHeader>
									<List>
										{alreadySelectedExercises.map(exercise => (
											<ExerciseCard key={exercise.id} exercise={exercise} draggable={false} uiStyle="row" disabled />
										))}
									</List>
								</>
							)}
						</ScrollContainer>
					</>
				)}
			</>
		);
	}
	return <Container>{body}</Container>;
};

const sharedPadding = css`
	padding: 0 20px;
`;

const StyledNetworkError = styled(NetworkError)`
	place-content: center;
	flex: 1;
	padding: 32px;
`;

const StyledLoadingContainer = styled(LoadingContainer)`
	place-content: center;
	flex: 1;
`;

const Container = styled.div`
	padding-top: 12px;
	${stack(12, "flexstart", "stretch")};
	flex: 1;
	min-height: 0px;
`;

const Section = styled.div`
	${sharedPadding}
`;

const StyledExerciseLibraryCount = styled(ExerciseLibraryCounts)`
	margin-bottom: 12px;
`;

const AlreadySelectedHeader = styled.div`
	margin-bottom: 12px;
`;

const Sort = styled.div`
	${row(12, "flexstart", "center")}
	${sharedPadding}
`;

const EmptySearchContainer = styled.div`
	align-items: center;
	justify-content: center;
	display: flex;
	flex-grow: 1;
	padding: 72px;
`;

const List = styled.div`
	${stack(undefined, "flexstart", "stretch")};
	padding: 0 20px 8px 20px;
	min-height: 0;
	@supports not (-moz-appearance: none) {
		padding-right: ${12}px;
		margin-right: 4px;
	}
	> * {
		margin-bottom: 8px;
	}
`;

const TooltipAnchor = styled.div`
	height: 0;
	margin: 0;
	padding: 0;
`;

const StyledTooltip = styled(Tooltip)`
	font-size: 16px;
`;

const ScrollContainer = styled.div`
	${getScrollbarStyle(8)};
	overflow-y: auto;
	flex: 1 1 auto;
`;

const IconContainer = styled.div`
	display: flex;
`;

const AddExerciseGuideTooltip = styled(Tooltip)`
	padding: 0;
	.tippy-arrow {
		color: ${colors.onboardingPrimary};
	}
	&[data-placement^="right"] > .tippy-arrow {
		top: 50% !important;
		bottom: auto !important;
		transform: translateY(-50%) !important;
		margin-top: 0 !important;
		margin-bottom: 0 !important;
	}
	.tippy-content {
		padding: 0;
	}
`;

